import { codeFromCodeableConcept } from '@seen/core/codeableConcept'
import type { ExtendedAllergy } from '@seen/core/models/allergies'
import { Badge } from '@seen/ui'
import type { JSX } from 'react'
import { ClinicalHoverLayout } from '#src/components/clinical/ClinicalHoverLayout.tsx'
import {
  ALLERGY_STATUS_COLORS,
  ALLERGY_TYPE_LABELS,
  SEVERITY_BADGE_COLORS,
} from '#src/components/clinical/statusConfigs.ts'
import { SanitizedHTML } from '#src/components/core/SanitizedHTML.tsx'
import { LabelColor } from '#src/components/LabelColor.tsx'
import { RowBadge } from '#src/components/TaskRowBadge.tsx'

export function AllergyCard({
  allergy,
  to,
}: {
  allergy: ExtendedAllergy
  to?: string
}): JSX.Element {
  const clinicalStatusCode =
    codeFromCodeableConcept(allergy.clinicalStatus) ?? ''
  const hasReaction = allergy.reaction !== 'No reaction recorded'

  return (
    <ClinicalHoverLayout
      to={to}
      title={
        <h3 className="text-[15px] leading-snug font-semibold">
          {allergy.name}
        </h3>
      }
      status={
        <RowBadge
          icon={
            <LabelColor
              color={ALLERGY_STATUS_COLORS[clinicalStatusCode] ?? '#bec2c8'}
            />
          }
          className="pointer-events-none"
        >
          {clinicalStatusCode || 'status'}
        </RowBadge>
      }
      metadata={
        <>
          {allergy.type && (
            <Badge variant="outline">
              {ALLERGY_TYPE_LABELS[allergy.type] ?? 'Type'}
            </Badge>
          )}
          {allergy.severity && (
            <Badge
              variant="outline"
              color={SEVERITY_BADGE_COLORS[allergy.severity]}
            >
              {allergy.severity}
            </Badge>
          )}
        </>
      }
    >
      {hasReaction && (
        <SanitizedHTML
          htmlString={allergy.reaction}
          inline
          className="text-xs text-foreground-secondary"
        />
      )}
    </ClinicalHoverLayout>
  )
}
